import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { RailSlotContext } from './railSlot'
import { ErrorBoundary } from './ErrorBoundary'
import { BrandSprite } from '../brand/sprite'
import { Icon, Mark } from '../brand/Icon'
import { useBrandName } from '../brand/pack'
import { useT } from '../i18n'

// Авто-сворачивание рельса: узкий десктоп → иконочный режим. Ручной тумблер
// переопределяет до следующего пересечения брейкпоинта.
const AUTO_QUERY = '(max-width: 1180px)'
const MOBILE_QUERY = '(max-width: 767px)'

export function AppLayout() {
  const t = useT()
  // Имя бренда — из пака домена; ключ каталога остаётся запасным (см. brand/pack).
  const brandName = useBrandName()
  const [collapsed, setCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [toolsEl, setToolsEl] = useState<HTMLElement | null>(null)

  // авто-сворачивание по ширине окна
  useEffect(() => {
    const mq = window.matchMedia(AUTO_QUERY)
    const apply = () => setCollapsed(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // мобильный режим (рельс → off-canvas drawer)
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY)
    const apply = () => setIsMobile(mq.matches)
    apply()
    mq.addEventListener('change', apply)
    return () => mq.removeEventListener('change', apply)
  }, [])

  // на мобиле содержимое рельса всегда развёрнуто (drawer во всю ширину)
  const contentCollapsed = !isMobile && collapsed

  return (
    <RailSlotContext.Provider value={toolsEl}>
      <BrandSprite />
      <div className="flex h-screen w-full overflow-hidden bg-abyss">
        {/* затемнение под мобильным drawer */}
        {drawerOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        {/* Единый рельс: статичный на десктопе, off-canvas drawer на мобиле */}
        <aside
          className={[
            'z-50 shrink-0 transition-[width,transform] duration-200 md:z-auto',
            'fixed inset-y-0 left-0 w-64 md:static',
            drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
            contentCollapsed ? 'md:w-16' : 'md:w-64',
          ].join(' ')}
        >
          <Sidebar
            collapsed={contentCollapsed}
            onToggle={() => (isMobile ? setDrawerOpen(false) : setCollapsed(v => !v))}
            onNavigate={() => setDrawerOpen(false)}
            toolsRef={setToolsEl}
          />
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Мобильная шапка */}
          <header className="flex items-center gap-3 border-b border-hull bg-deep px-4 py-3 text-foam md:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('nav.menu')}
              className="rounded-lg p-1 text-mist hover:bg-haze hover:text-foam"
            >
              <Icon name="ic-panel" className="h-6 w-6" />
            </button>
            <Mark className="h-6 w-6" />
            <span className="font-heading text-[16px] font-extrabold tracking-tight">
              {brandName}
            </span>
          </header>

          <main className="min-h-0 flex-1 overflow-auto">
            {/* Падение раздела не должно уносить каркас: рельс и переключатель
                кабинета остаются живыми, чтобы было куда уйти. */}
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </RailSlotContext.Provider>
  )
}
